<template>
    <div class="project-prediction-container mt-4">
        <div class="row mt-2 mb-3">
            <div class="col-lg-5 col-md-8 col-sm-11 mx-auto">
                <icon-with-message img-src="/img/picto-lamp-red.svg" title="Hint" text-color="#3040C4">
                    <template v-slot:content>
                        We analyzed {{ project.num_of_rows }} records.
                        We can make a prediction for <b>{{ predictionColumnName }}</b> based on the data you will
                        provide in the following columns:
                    </template>
                </icon-with-message>
            </div>
        </div>
        <div class="row mb-5">
            <div class="col-lg-8 col-md-12 mx-auto">
                <div class="alert alert-primary project-creation-info-message" role="alert">
                    Curious about how prediction works? Please read carefully our
                    <b><a href="https://medium.com/@elinamakri/1d11efc3e7c2?" target="_blank">Example of the Prediction recipe article!</a></b>
                </div>
            </div>
        </div>
        <data-table v-bind:data="{headers: columnsForPrediction}" v-bind:data-table="false">
            <template v-slot:additionalRows>
                <tr v-for="(row) in dataRows">
                    <td v-for="(column) in row">
                        <input class="form-control" v-bind:class="{ 'is-invalid': !column.value && formSubmitted }"
                               :type="column.inputType" v-model="column.value">
                    </td>
                </tr>
            </template>
        </data-table>
        <div class="row mt-2 mb-2">
            <div class="col-md-5 col-sm-11 mx-auto">
                <div class="text-danger" v-for="error in errors">
                    {{ error }}
                </div>
            </div>
        </div>
        <div class="row mt-2 mb-2" v-if="warningMessage">
            <div class="col-lg-5 col-md-8 col-sm-11 mx-auto">
                <icon-with-message img-src="/img/warning.svg" title="Warning" text-color="#f6993f">
                    <template v-slot:content>
                        {{ warningMessage }}
                    </template>
                </icon-with-message>
            </div>
        </div>
        <div class="row mt-2 mb-2" v-if="successMessage">
            <div class="col-lg-5 col-md-8 col-sm-11 mx-auto">
                <icon-with-message img-src="/img/check.svg" title="Success" text-color="#38c172">
                    <template v-slot:content>
                        {{ successMessage }}
                    </template>
                </icon-with-message>
            </div>
        </div>
        <button class="mt-4 mb-3 col-md-5 col-sm-10 btn btn-primary" @click="predict">Predict {{ predictionColumnName }}
        </button>
        <modal-component></modal-component>
        <project-results-preview-data v-bind:preview-data="previewData" v-bind:number-of-rows="numberOfRows"></project-results-preview-data>
    </div>
</template>
<script>
    import {mapActions, mapGetters} from 'vuex';
    import IconWithMessage from "./IconWithMessage";

    export default {
        components: {IconWithMessage},
        props: {
            project: Object
        },
        computed: {
            columnsForPrediction: {
                get: function () {
                    return this.project.model_columns.filter(column => {
                        return column.columnIndex !== parseInt(this.project.prediction_column_index)
                    });
                }
            },
            predictionColumnName: function () {
                return '"' + this.project.model_columns[this.project.prediction_column_index].value + '"';
            }
        },
        data: function () {
            return {
                formSubmitted: false,
                dataRows: [],
                errors: [],
                numberOfRows: 0,
                previewData: {
                    headers: [],
                    rows: []
                },
                warningMessage: null,
                successMessage: null
            }
        },
        created() {
        },
        mounted() {
            this.dataRows.push(this.getNewRow());
            this.runAnalysis();
        },
        methods: {
            ...mapActions([
                'closeModal',
                'openModal',
                'setLoading',
                'setMessage',
                'handleError',
                'requestRecipeFromAPI',
                'setModalAllowClose'
            ]),
            getNewRow() {
                return this.columnsForPrediction.map((column, index) => {
                    return {
                        columnIndex: column.columnIndex,
                        columnName: column.value,
                        inputType: this.getInputType(column.columnIndex),
                        value: null
                    }
                });
            },
            getInputType(index) {
                switch (this.project.model_columns[index].type) {
                    case 'Nominal':
                        return 'text';
                    case 'Integer':
                        return 'number';
                    case 'Float':
                        return 'number';
                    case 'Percentage':
                        return 'number';
                    default:
                        return 'text';
                }
            },
            runAnalysis() {
                let instance = this;
                instance.setLoading(true);
                instance.openModal();
                instance.requestRecipeFromAPI(instance.project)
                    .then(function (response) {
                        const rows = response.data.parsedCSV.rows;
                        instance.numberOfRows = response.data.parsedCSV.numberOfRows;
                        instance.previewData.headers = response.data.parsedCSV.headers;
                        instance.previewData.rows = rows;
                        if(response.data.confidence < 0.5)
                            instance.warningMessage = response.data.message;
                        else
                            instance.successMessage = response.data.message;
                    })
                    .catch(function (error) {
                        instance.handleError(instance, error)
                    });
            },
            predict() {
                this.errors = [];
                this.formSubmitted = true;

                if (!this.isDataValid())
                    this.errors.push('Please fill in the missing columns.');
                else
                    this.makePredictionCall();
            },
            makePredictionCall() {
                let dataToSend = {
                    dataRows: []
                };

                for (let i = 0; i < this.dataRows.length; i++) {
                    dataToSend.dataRows.push({
                        rowIndex: i,
                        values: []
                    });
                    for (let j = 0; j < this.dataRows[i].length; j++) {
                        let value = this.dataRows[i][j].value;
                        if (this.project.model_columns[this.dataRows[i][j].columnIndex].type === 'Percentage')
                            value += '%';
                        dataToSend.dataRows[i].values.push({
                            columnIndex: this.dataRows[i][j].columnIndex,
                            value: value
                        })
                    }
                }
                let instance = this;

                instance.setLoading(true);
                instance.setMessage(null);
                instance.openModal();
                instance.setModalAllowClose(true);

                axios.post(
                    process.env.MIX_API_URL + instance.project.recipe.api_path + '/predict?projectId=' +
                    instance.project.code,
                    dataToSend, {headers: {'Bearer': process.env.MIX_API_AUTH_TOKEN}})
                    .then(function (response) {
                        const result = instance.roundValueIfFloat(response.data[0].prediction);
                        instance.setLoading(false);
                        instance.openModal();
                        instance.setMessage(
                            'Prediction for ' + instance.predictionColumnName + ': <b>' +
                            result + '</b>'
                        );
                    })
                    .catch(function (error) {
                        instance.handleError(error)
                    });
            },
            isDataValid() {
                for (let i = 0; i < this.dataRows.length; i++) {
                    for (let j = 0; j < this.dataRows[i].length; j++) {
                        if (!this.dataRows[i][j].value)
                            return false;
                    }
                }
                return true;
            },
            roundValueIfFloat(prediction) {
                if(this.isFloat(prediction)) {
                    return Number(prediction).toFixed(4);
                }
                return  prediction;
            },
            isFloat(n){
                return Number(n) == n && n % 1 !== 0;
            }
        }
    }
</script>
<style lang="scss">

    @import "../../sass/variables";

    input {
        width: 100%;
    }

</style>
